import React, { useState, useEffect, useMemo } from "react";
import useAssetProvider from "../useAssetProvider";
import { useStyles } from "./Style";
import {
	InputLabel,
	MenuItem,
	Select,
	Typography,
	Grid,
	FormControl,
	TextField,
} from "@material-ui/core";
import VendorSearch from "../Vendor/VendorSearch";

export default function NewFee({ fee, setFee }) {
    const classes = useStyles();
    const { assets, feeTypes } = useAssetProvider();
    const [vendor, setVendor] = useState({});
    const [selectedVendorValue, setSelectedVendorValue] = useState("");

  const LESSOR_VENDOR = useMemo(
    () => ({
      short_id: "ACC-229914",
      name: "NMEF Pass Through Sales Tax",
      street: "",
      city: "",
      state: "",
      zip_code: "",
      country: "",
    }),
    []
  );

  // Build deal vendor picklist from assets (unique, sorted by label)
  const dealVendorOptions = useMemo(() => {
    const uniqueVendors = new Map();

    const addVendor = (candidate = {}) => {
      const shortId = candidate.short_id || candidate.vendor_id || candidate.vendor || "";
      const id = candidate.id || shortId || candidate.name || "";
      const name = candidate.name || candidate.vendor_name || candidate.vendor || "";
      const key = shortId || id || name;
      if (!key || uniqueVendors.has(key)) return;

      const street = candidate.street || candidate.vendor_street || "";
      const city = candidate.city || candidate.vendor_city || "";
      const state = candidate.state || candidate.vendor_state || "";
      const zip = candidate.zip_code || candidate.vendor_zipcode || candidate.vendor_zip || "";
      const country = candidate.country || candidate.vendor_country || "";

      const label = [shortId, name].filter(Boolean).join(" ").trim();

      uniqueVendors.set(key, {
        value: shortId || id || name,
        label: label || shortId || name || "Vendor",
        data: {
          id,
          short_id: shortId,
          name,
          street,
          city,
          state,
          zip_code: zip,
          country,
        },
      });
    };

    if (Array.isArray(assets)) {
      assets.forEach((asset = {}) =>
        addVendor({
          id: asset.vendor_id || asset.vendor || asset.vendor_name,
          short_id: asset.vendor_id || asset.vendor || "",
          name: asset.vendor_name || asset.vendor || "",
          street: asset.vendor_street || "",
          city: asset.vendor_city || "",
          state: asset.vendor_state || "",
          zip_code: asset.vendor_zip || asset.vendor_zipcode || "",
          country: asset.vendor_country || "",
        })
      );
    }

    return Array.from(uniqueVendors.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [assets]);

	const warranty_underWritter = [
		"Truckmaster",
		"Truck Master Warranty Program",
		"TCP/NTP(Truckers Choice Protection)",
		"Select",
		"Ryder",
		"Rush Care",
		"Premium2000",
		"Other",
		"Manufacture",
		"International's System 1 Warranty",
	];

	useEffect(() => {
		setFee((prevFee) => ({
			...prevFee,
			vendor: vendor.short_id || vendor.vendor || prevFee.vendor || "",
			vendor_name: vendor.name || vendor.vendor_name || prevFee.vendor_name || "",
			vendor_street: vendor.street || vendor.vendor_street || prevFee.vendor_street || "",
			vendor_city: vendor.city || vendor.vendor_city || prevFee.vendor_city || "",
			vendor_state: vendor.state || vendor.vendor_state || prevFee.vendor_state || "",
			vendor_zipcode: vendor.zip_code || vendor.vendor_zipcode || prevFee.vendor_zipcode || "",
			vendor_country: vendor.country || vendor.vendor_country || prevFee.vendor_country || "",
		}));
	}, [vendor, setFee]);

	useEffect(() => {
		if (!assets?.length) {
			return;
		}
		const primaryAsset = assets[0];
		const assetWithHighestPrice = assets.reduce(
			(acc, item) => (acc.price > item.price ? acc : item),
			primaryAsset
		);
		const vendorInfo = {
			short_id: assetWithHighestPrice?.vendor_id || "",
			name: assetWithHighestPrice?.vendor_name || assetWithHighestPrice?.vendor || "",
			street: assetWithHighestPrice?.vendor_street || "",
			city: assetWithHighestPrice?.vendor_city || "",
			state: assetWithHighestPrice?.vendor_state || "",
			zip_code: assetWithHighestPrice?.vendor_zip || assetWithHighestPrice?.vendor_zipcode || "",
			country: assetWithHighestPrice?.vendor_country || "",
		};
		setFee((prevFee) => ({
			...prevFee,
			vendor: vendorInfo.short_id || prevFee.vendor || "",
			vendor_name: vendorInfo.name || prevFee.vendor_name || "",
			vendor_street: vendorInfo.street || prevFee.vendor_street || "",
			vendor_city: vendorInfo.city || prevFee.vendor_city || "",
			vendor_state: vendorInfo.state || prevFee.vendor_state || "",
			vendor_zipcode: vendorInfo.zip_code || prevFee.vendor_zipcode || "",
			vendor_country: vendorInfo.country || prevFee.vendor_country || "",
      payable_to: prevFee.payable_to || "Vendor",
			application_id: primaryAsset?.application_id || prevFee.application_id || "",
		}));
		setVendor(vendorInfo);
	}, [assets, setFee]);

  // Keep the Select value in sync when payable_to is Vendor
  useEffect(() => {
    if (fee.payable_to !== "Vendor") {
      setSelectedVendorValue("");
      return;
    }
    const candidateValue = vendor.short_id || vendor.id || vendor.name || vendor.vendor_name || "";
    if (candidateValue && dealVendorOptions.some((option) => option.value === candidateValue)) {
      setSelectedVendorValue(candidateValue);
    } else {
      setSelectedVendorValue("");
    }
  }, [dealVendorOptions, fee.payable_to, vendor]);

  const handlePayable = (e) => {
    const payableTo = e.target.value;
    setFee({ ...fee, payable_to: payableTo });

    if (payableTo === "Lessor") {
      // Force NMEF vendor
      setVendor({ ...LESSOR_VENDOR });
      return;
    }

    if (payableTo === "Vendor") {
      const currentValue = vendor.short_id || vendor.id || vendor.name || vendor.vendor_name || "";
      const existingOption = dealVendorOptions.find((option) => option.value === currentValue);
      if (existingOption) {
        setVendor({ ...existingOption.data });
        return;
      }
      if (dealVendorOptions.length) {
        setVendor({ ...dealVendorOptions[0].data });
      } else {
        setVendor({});
      }
      return;
    }

    // Other: clear and let VendorSearch fill
    setVendor({});
  };

  const handleVendorSelect = (event) => {
    const value = event.target.value;
    setSelectedVendorValue(value);
    const option = dealVendorOptions.find((candidate) => candidate.value === value);
    if (option) {
      setVendor({ ...option.data });
    } else {
      setVendor({});
    }
  };

  const renderVendorField = () => {
    const vendorDisplayName = [vendor.short_id, vendor.name || vendor.vendor_name]
      .filter(Boolean)
      .join(" ");

    if (fee.payable_to === "Lessor") {
      return (
        <TextField
          style={{ width: "100%" }}
          id="vendor-lessor"
          label="Vendor"
          value={`${LESSOR_VENDOR.short_id} ${LESSOR_VENDOR.name}`}
          margin="normal"
          disabled
        />
      );
    }

    if (fee.payable_to === "Vendor") {
      return (
        <FormControl className={classes.formControl} style={{ width: "100%" }}>
          <InputLabel id="vendorSelectLabel">Vendor</InputLabel>
          <Select
            style={{ width: "100%" }}
            labelId="vendorSelectLabel"
            id="vendorSelect"
            value={selectedVendorValue}
            onChange={handleVendorSelect}
          >
            {dealVendorOptions.length === 0 && (
              <MenuItem value="" disabled>
                No associated vendors
              </MenuItem>
            )}
            {dealVendorOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (fee.payable_to === "Other") {
      return (
        <div style={{ width: "100%", marginTop: 8 }}>
          <Typography
            variant="caption"
            style={{ display: "block", marginBottom: 4, color: "rgba(0,0,0,0.54)" }}
          >
            Vendor
          </Typography>
          <VendorSearch
            notSupported={false}
            vendor={vendor}
            newAddedvendor={vendor}
            setVendor={setVendor}
            isEdit={true}
            updateFeeVendor={setVendor}
            type="fee"
          />
        </div>
      );
    }

    // Default (initial)
    return (
      <TextField
        style={{ width: "100%" }}
        id="vendor"
        label="Vendor"
        value={vendorDisplayName}
        margin="normal"
        disabled
      />
    );
  };

	return (
		<Grid
			container
			direction="column"
			//justify="center"
			alignItems="center"
			style={{ padding: "1rem" }}>
			<Grid item xs={12} style={{ textAlign: "center" }}>
				<Typography variant="h6">Add Fee</Typography>
			</Grid>
			<Grid item xs={12} style={{ width: "100%", marginTop: "0.5rem" }}>
				<FormControl className={classes.formControl} style={{ width: "100%" }}>
					<InputLabel id="payableToLabel">Payable To</InputLabel>
					<Select
						style={{ width: "100%" }}
						id="payableTo"
						labelId="payableToLabel"
						value={fee.payable_to || ""}
						onChange={handlePayable}
					>
						<MenuItem value="Lessor">Lessor</MenuItem>
						<MenuItem value="Vendor">Vendor</MenuItem>
						<MenuItem value="Other">Other</MenuItem>
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={12} style={{ width: "100%", marginTop: "0.5rem" }}>
				<TextField
					style={{ width: "100%" }}
					id="ammount"
					label="Amount"
					value={fee.amount || ""}
					margin="normal"
					onChange={(e) =>
						setFee({
							...fee,
							amount: e.target.value,
							primary_amount: e.target.value,
						})
					}
				/>
			</Grid>
			<Grid item xs={12} style={{ width: "100%", marginTop: "0.5rem" }}>
				<FormControl className={classes.formControl} style={{ width: "100%" }}>
					<InputLabel id="feeTypeLabel">Type</InputLabel>
					<Select
						style={{ width: "100%" }}
						id="feeType"
						labelId="feeTypeLabel"
						value={fee.type || ""}
						onChange={(e) => {
							setFee({ ...fee, type: e.target.value });
						}}>
						{feeTypes.map((type, index) => (
							<MenuItem key={index} value={type.value}>
								{type.value}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>

      {/* Warranty Underwriter */}
      {fee.type === "Warranty" && (
        <Grid item xs={12} style={{ width: "100%", marginTop: "0.5rem" }}>
          <FormControl className={classes.formControl} style={{ width: "100%" }}>
            <InputLabel id="warranty_underwriterLabel">Warranty Underwriter</InputLabel>
            <Select
              style={{ width: "100%" }}
              id="warranty_underwriter"
              labelId="warranty_underwriterLabel"
              value={fee.warranty_vendor || ""}
              onChange={(e) => {
                setFee({ ...fee, warranty_vendor: e.target.value });
              }}
            >
              {warranty_underWritter.map((warranty) => (
                <MenuItem key={warranty} value={warranty}>
                  {warranty}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

			{fee.type === "Warranty" &&
				(fee.warranty_vendor === "Other" ||
					fee.warranty_vendor === "Manufacture") && (
					<Grid item xs={12} style={{ width: "100%", marginTop: "0.5rem" }}>
						<TextField
							id="details"
							label="Details"
							variant="outlined"
							multiline
							rows={4}
							style={{ width: "100%" }}
							defaultValue={fee.warranty_vendor_other_detail || ""}
						/>
					</Grid>
				)}

      {/* Vendor — uses payable_to-driven renderer */}
      <Grid item container xs={12} style={{ width: "100%", marginTop: "0.5rem" }}>
        <Grid item xs={12}>{renderVendorField()}</Grid>
      </Grid>
    </Grid>
  );
}
